import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Circular avatar with three fallback layers:
 *   1. user.avatarUrl loads → show it (referrerPolicy:no-referrer so
 *      Google profile-image CDNs don't reject the request)
 *   2. Image errors → show initials on a brand background
 *   3. No name either → generic User icon
 */
const Avatar = ({ user, size = 28 }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const url = user?.avatarUrl || user?.photoURL;
  const name = user?.displayName || user?.name || "";
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || (user?.email?.[0] || "U").toUpperCase();

  const dim = { width: size, height: size };

  if (url && !imgFailed) {
    return (
      <img
        src={url}
        alt={name || "User Avatar"}
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        loading="lazy"
        onError={() => setImgFailed(true)}
        style={dim}
        className="rounded-full object-cover bg-gray-100 shrink-0"
      />
    );
  }

  return (
    <div
      style={dim}
      className="rounded-full bg-gray-900 text-white flex items-center justify-center shrink-0 select-none"
      aria-hidden="true"
    >
      {initials ? (
        <span className="text-[11px] font-semibold tracking-tight">{initials}</span>
      ) : (
        <Icon name="User" size={Math.round(size * 0.5)} className="text-white" />
      )}
    </div>
  );
};

const SEARCH_PATH = "/advanced-search-discovery";

// Shared by the signed-in and signed-out variants — they had drifted apart, so
// the button lost its outline once you logged in. `ring` rather than `border`
// keeps the height identical to the adjacent Sign in / avatar controls.
const listPropertyBtn =
  "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold " +
  "text-gray-800 bg-gray-50 ring-1 ring-gray-200 hover:bg-gray-100 hover:ring-gray-300 transition";

const firstName = (user) => {
  const full = user?.displayName || user?.name || "";
  const first = full.trim().split(/\s+/)[0];
  if (first) return first;
  if (user?.email) return user.email.split("@")[0];
  if (user?.phone) return user.phone;
  return "Account";
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isOwner, isAdmin, logout } = useAuth();

  // ─── Primary nav: property categories ──────────────────────
  // Flat list, no dropdowns. Each `type` value is a member of the schema enum
  // in property.schema.ts, so the search page filters server-side without
  // going through its alias table.
  const categoryNav = [
    { name: "Flats",       path: "/advanced-search-discovery?type=flat",   icon: "Building2" },
    { name: "Houses",      path: "/advanced-search-discovery?type=house",  icon: "Home" },
    { name: "Rooms",       path: "/advanced-search-discovery?type=room",   icon: "DoorOpen" },
    { name: "PG / Hostel", path: "/advanced-search-discovery?type=pg",     icon: "Users" },
    { name: "Commercial",  path: "/advanced-search-discovery?type=office", icon: "Store" },
    // Unfiltered search — same page, no ?type, so it lands on all listings.
    { name: "Search",      path: "/advanced-search-discovery",            icon: "Search", outlined: true },
  ];

  // Same categories whether signed in or not — a marketplace header should
  // lead with inventory. Personal destinations live in the account menu.
  const navigationItems = categoryNav;

  // Secondary destinations, kept out of the header bar but surfaced in the
  // mobile drawer and the footer so nothing is orphaned.
  const secondaryNav = [
    { name: "Rent by city",  path: "/rent",         icon: "MapPin" },
    { name: "Agreements",    path: "/agreements",   icon: "FileText" },
    { name: "Blog & guides", path: "/blog",         icon: "BookOpen" },
    { name: "How it works",  path: "/about",        icon: "Info" },
    { name: "Help",          path: "/help-center",  icon: "HelpCircle" },
  ];

  // Account dropdown (always shown when authenticated)
  const accountItems = [
    { name: "My profile",   path: "/user-profile-verification-center", icon: "User" },
    { name: "Notifications", path: "/notifications",                   icon: "Bell" },
    { name: "Favorites",    path: "/favorites",                        icon: "Heart" },
    { name: "Bookings",     path: "/my-bookings",                      icon: "CalendarCheck" },
    { name: "Messages",     path: "/messages-communication",           icon: "MessageCircle" },
  ];
  const ownerItems = [
    { name: "Owner dashboard", path: "/owner-dashboard-suite",      icon: "BarChart3" },
    { name: "List a property", path: "/property-listing-creation",  icon: "Plus" },
  ];
  const adminItems = [
    { name: "Admin panel", path: "/admin-dashboard", icon: "Shield" },
  ];

  // Mobile-only extras for guests
  const mobilePublicExtra = [
    { name: "Contact us",       path: "/contact-us", icon: "Phone" },
    { name: "Safety guidelines", path: "/guidelines", icon: "ShieldCheck" },
  ];

  // Active path: exact match for top-level, prefix match for nested. Category
  // links share one pathname and differ only by ?type=, so when the link
  // carries a query we compare that too — otherwise all five would light up
  // together on the search page.
  const isActivePath = useCallback(
    (path) => {
      const p = location?.pathname || "";
      const [base, query] = path.split("?");
      if (base === "/") return p === "/";
      if (p !== base && !p.startsWith(base + "/")) return false;
      const current = (new URLSearchParams(location?.search || "").get("type") || "").toLowerCase().trim();
      if (!query) {
        // Bare "Search" shares its pathname with the five category links, so
        // it is only active when no type filter is applied — otherwise it
        // would light up alongside whichever category is selected.
        return base === SEARCH_PATH ? !current : true;
      }
      const target = (new URLSearchParams(query).get("type") || "").toLowerCase().trim();
      if (target === "flat") return current === "flat" || current === "apartment";
      if (target === "house") return current === "house" || current === "villa";
      if (target === "pg") return current === "pg" || current === "hostel";
      if (target === "office") return current === "office" || current === "commercial" || current === "shop" || current === "warehouse";
      return current === target;
    },
    [location?.pathname, location?.search],
  );

  // ─── Effects ───────────────────────────────────────────────
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  }, [location?.pathname, location?.search]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setShowUserMenu(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  // Close user menu on outside click (more robust than a single overlay div)
  useEffect(() => {
    if (!showUserMenu) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showUserMenu]);

  // Subtle elevation on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignIn = () => navigate("/user-registration-login");
  const handleListProperty = () => navigate("/property-listing-creation");
  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate("/");
  };

  // Reusable nav-link className (desktop pill)
  // `outlined` items carry a visible grey field + border to set them apart from
  // the plain category links. Every variant keeps a border (transparent when
  // unused) so the outlined one doesn't sit 2px taller than its neighbours.
  const navLink = (active, outlined = false) => {
    const base =
      "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm transition border";
    if (outlined) {
      return `${base} ${
        active
          ? "bg-gray-100 border-gray-300 text-gray-900 font-semibold"
          : "bg-gray-50 border-gray-200 text-gray-700 font-medium hover:bg-gray-100 hover:border-gray-300 hover:text-gray-900"
      }`;
    }
    return `${base} border-transparent ${
      active
        ? "bg-gray-100 text-gray-900 font-semibold"
        : "text-gray-700 hover:text-gray-900 hover:bg-gray-50 font-medium"
    }`;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md transition-shadow ${
        scrolled
          ? "shadow-[0_1px_0_rgba(17,24,39,0.06),0_8px_24px_-12px_rgba(17,24,39,0.10)] border-b border-transparent"
          : "border-b border-gray-100"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo pinned left; nav + actions travel together on the right via
            ml-auto on the first of them, rather than justify-between spreading
            the nav into the middle. */}
        <div className="flex items-center h-16">
          {/* Brand */}
          {/*
            Mark and wordmark are separate images and the tagline is real text.
            In the flattened artwork the tagline is only 28px of 394 (7%), so at
            header scale it renders ~3px tall and is unreadable — as HTML it
            stays crisp and can be sized independently of the wordmark.
          */}
          <Link
            to="/"
            aria-label="Toletforrent — find your perfect home"
            className="shrink-0 flex items-center gap-2 sm:gap-2.5 hover:opacity-90 transition"
          >
            <img
              src="/assets/images/logo-mark.png"
              alt=""
              width={152}
              height={160}
              className="h-9 w-auto sm:h-11"
            />
            <span className="flex flex-col justify-center leading-none">
              <img
                src="/assets/images/logo-wordmark.png"
                alt=""
                width={614}
                height={96}
                className="h-[19px] w-auto sm:h-[23px]"
              />
              <span className="mt-[3px] text-[8px] sm:text-[9px] font-semibold uppercase tracking-[0.16em] text-gray-500 whitespace-nowrap">
                Find your perfect home
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-2 ml-auto" aria-label="Primary">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={navLink(isActivePath(item.path), item.outlined)}
              >
                <Icon name={item.icon} size={16} />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop right side */}
          <div className="hidden lg:flex items-center gap-2 ml-3">
            {isAuthenticated ? (
              <>
                {/* List a property — visible from lg, not xl */}
                {(isOwner || !isOwner) && (
                  <button
                    onClick={handleListProperty}
                    className={listPropertyBtn}
                  >
                    List property
                  </button>
                )}

                {/* Notifications */}
                <Link
                  to="/notifications"
                  className="relative p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition"
                  title="Notifications"
                  aria-label="Notifications"
                >
                  <Icon name="Bell" size={20} />
                  {/* Unread dot — wire to real count when API exists */}
                  {/* <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" /> */}
                </Link>

                {/* Account pill */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu((v) => !v)}
                    className="flex items-center gap-2 pl-2.5 pr-1.5 py-1 rounded-full border border-gray-200 hover:shadow-[0_2px_8px_rgba(17,24,39,0.10)] transition bg-white"
                    aria-haspopup="menu"
                    aria-expanded={showUserMenu}
                    aria-label="Account menu"
                  >
                    <Icon name="Menu" size={14} className="text-gray-600" />
                    <span className="hidden md:inline text-sm font-medium text-gray-800 max-w-[110px] truncate pr-0.5">
                      {firstName(user)}
                    </span>
                    <Avatar user={user} size={30} />
                  </button>

                  {showUserMenu && (
                    <div
                      role="menu"
                      className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-[0_24px_48px_-12px_rgba(17,24,39,0.18)] overflow-hidden"
                    >
                      {/* User card */}
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {user?.displayName || user?.name || "Welcome"}
                        </p>
                        {(user?.email || user?.phone) && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {user?.email || user?.phone}
                          </p>
                        )}
                      </div>

                      <div className="py-1.5">
                        {accountItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <Icon name={item.icon} size={16} className="text-gray-400" />
                            {item.name}
                          </Link>
                        ))}
                      </div>

                      {(isOwner || isAdmin) && (
                        <>
                          <div className="border-t border-gray-100" />
                          <div className="py-1.5">
                            {(isOwner || isAdmin) && ownerItems.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                                onClick={() => setShowUserMenu(false)}
                              >
                                <Icon name={item.icon} size={16} className="text-rose-500" />
                                {item.name}
                              </Link>
                            ))}
                            {isAdmin && adminItems.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                                onClick={() => setShowUserMenu(false)}
                              >
                                <Icon name={item.icon} size={16} className="text-gray-500" />
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        </>
                      )}

                      <div className="border-t border-gray-100" />
                      <Link
                        to="/help-center"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Icon name="HelpCircle" size={16} className="text-gray-400" />
                        Help center
                      </Link>

                      <div className="border-t border-gray-100" />
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition text-left"
                      >
                        <Icon name="LogOut" size={16} className="text-rose-500" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={handleListProperty}
                  className={listPropertyBtn}
                >
                  List property
                </button>
                <button
                  onClick={handleSignIn}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 transition shadow-[0_2px_8px_rgba(244,63,94,0.25)]"
                >
                  Sign in
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            className="lg:hidden ml-auto p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={22} />
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed top-16 left-0 right-0 z-50 bg-white border-t border-gray-100 shadow-[0_24px_48px_-12px_rgba(17,24,39,0.18)] transform transition-all duration-200 ${
          isMobileMenuOpen ? "translate-y-0 opacity-100" : "-translate-y-2 pointer-events-none opacity-0"
        }`}
      >
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto py-4 px-4 space-y-1">
          {/* User card */}
          {isAuthenticated && (
            <div className="flex items-center gap-3 px-4 py-3 mb-2 bg-gray-50 rounded-2xl">
              <Avatar user={user} size={40} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.displayName || user?.name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email || user?.phone || ""}</p>
              </div>
              <Link
                to="/notifications"
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition"
                aria-label="Notifications"
              >
                <Icon name="Bell" size={18} />
              </Link>
            </div>
          )}

          {/* Primary nav */}
          <p className="px-2 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Menu</p>
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition ${
                isActivePath(item.path)
                  ? "bg-rose-50 text-rose-600 font-semibold"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-50 font-medium"
              }`}
            >
              <Icon name={item.icon} size={18} />
              <span>{item.name}</span>
            </Link>
          ))}

          {/* Account block */}
          {isAuthenticated && (
            <>
              <div className="border-t border-gray-100 my-3" />
              <p className="px-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Account</p>
              {[
                { name: "My profile",    path: "/user-profile-verification-center", icon: "User" },
                { name: "Notifications", path: "/notifications",                    icon: "Bell" },
              ].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition"
                >
                  <Icon name={item.icon} size={18} />
                  {item.name}
                </Link>
              ))}

              {(isOwner || isAdmin) && (
                <>
                  <div className="border-t border-gray-100 my-3" />
                  <p className="px-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Owner</p>
                  {ownerItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition"
                    >
                      <Icon name={item.icon} size={18} />
                      {item.name}
                    </Link>
                  ))}
                </>
              )}

              {isAdmin && (
                <>
                  <div className="border-t border-gray-100 my-3" />
                  {adminItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition"
                    >
                      <Icon name={item.icon} size={18} />
                      {item.name}
                    </Link>
                  ))}
                </>
              )}
            </>
          )}

          {/* Secondary destinations — not in the header bar, always in the drawer */}
          <div className="border-t border-gray-100 my-3" />
          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Explore
          </p>
          {secondaryNav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition"
            >
              <Icon name={item.icon} size={18} />
              <span>{item.name}</span>
            </Link>
          ))}

          {/* Guest extras */}
          {!isAuthenticated && (
            <>
              <div className="border-t border-gray-100 my-3" />
              {mobilePublicExtra.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition"
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.name}</span>
                </Link>
              ))}
            </>
          )}

          {/* CTA stack */}
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); handleListProperty(); }}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 transition"
                >
                  <Icon name="Plus" size={14} />
                  List property
                </button>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-sm font-medium text-rose-600 border border-rose-200 hover:bg-rose-50 transition"
                >
                  <Icon name="LogOut" size={14} />
                  Sign out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); handleSignIn(); }}
                  className="w-full px-4 py-3 rounded-full text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 transition"
                >
                  Sign in
                </button>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); handleListProperty(); }}
                  className="w-full px-4 py-3 rounded-full text-sm font-medium text-gray-800 border border-gray-200 hover:bg-gray-50 transition"
                >
                  List property
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
